import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOut, ChevronRight, User as UserIcon, Settings as SettingsIcon, Sun, Moon, Monitor, ShieldOff } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CmdKHint } from "./CommandPalette";
import CmdKFirstSessionHint from "./CmdKFirstSessionHint";
import { RoleBadge } from "./RoleBadge";
import NotificationsBell from "./NotificationsBell";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import api, { REFRESH_KEY, formatApiErrorDetail } from "../lib/api";

const BREADCRUMB_MAP = {
  "/dashboard": ["Dashboard"],
  "/insights": ["Insights"],
  "/cases": ["Cases"],
  "/team-members": ["Team Members"],
  "/notifications": ["Notifications"],
  "/audit-logs": ["Audit Log"],
  "/compliance": ["Compliance"],
  "/profile": ["My profile"],
  "/settings/headcount": ["Settings", "Headcount"],
  "/settings/security": ["Settings", "Security"],
  "/settings/preferences": ["Settings", "Notifications"],
  "/admin/taxonomy": ["Admin", "Taxonomy"],
  "/admin/service-lines": ["Admin", "Service Lines"],
  "/admin/hr-partners": ["Admin", "HR Partners"],
  "/admin/workflow": ["Admin", "Workflow"],
  "/admin/notifications": ["Admin", "Notifications"],
  "/admin/doc-categories": ["Admin", "Document Categories"],
  "/admin/templates": ["Admin", "Templates"],
  "/admin/users": ["Admin", "Users"],
};

function ThemeQuickToggle() {
  const { preference, setPreference, loading } = useTheme();
  const options = [
    { value: "light", label: "Light", Icon: Sun },
    { value: "dark", label: "Dark", Icon: Moon },
    { value: "system", label: "System", Icon: Monitor },
  ];
  return (
    <div
      data-testid="theme-quick-toggle"
      className="px-2 pb-1 pt-2"
    >
      <div className="mb-1.5 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-[color:var(--page-fg-muted)]">
        <span>Theme</span>
        {loading && <span className="text-brand-teal">saving…</span>}
      </div>
      <div
        role="radiogroup"
        aria-label="Theme preference"
        className="flex items-center gap-1 rounded-xl bg-[color:var(--surface-2)] p-1 dark:bg-white/5"
      >
        {options.map(({ value, label, Icon }) => {
          const active = preference === value;
          return (
            <button
              key={value}
              role="radio"
              aria-checked={active}
              type="button"
              data-testid={`theme-option-${value}`}
              onClick={() => setPreference(value)}
              className={
                "flex flex-1 items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-medium transition " +
                (active
                  ? "bg-white text-[color:var(--page-fg)] shadow-brand-sm dark:bg-slate-800 dark:text-slate-100"
                  : "text-[color:var(--page-fg-muted)] hover:text-[color:var(--page-fg)] dark:hover:text-slate-200")
              }
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}


function breadcrumbsFor(pathname) {
  if (pathname.startsWith("/cases/") && pathname.length > "/cases/".length) {
    return ["Cases", "Detail"];
  }
  return BREADCRUMB_MAP[pathname] || ["Cases"];
}

/**
 * Phase 8 — Goodwin Resolve topbar.
 *
 * White surface with subtle bottom border. Charcoal breadcrumbs, navy
 * role pill, brand-green Admin badge.
 */
export function TopBar({ onUnreadChange }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const crumbs = breadcrumbsFor(location.pathname);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // Phase 13 — "Sign me out of all other devices"
  const [signOutDialogOpen, setSignOutDialogOpen] = React.useState(false);
  const [signOutBusy, setSignOutBusy] = React.useState(false);

  const handleSignOutOthers = async () => {
    setSignOutBusy(true);
    try {
      const refresh_token = localStorage.getItem(REFRESH_KEY) || undefined;
      const { data } = await api.post("/auth/logout-all-others", {
        refresh_token,
      });
      const count = data?.revoked_sessions ?? 0;
      if (count === 0) {
        toast.info(data?.message || "No other active sessions to sign out.");
      } else {
        toast.success(
          data?.message ||
            `Signed out of ${count} other device${count === 1 ? "" : "s"}.`,
        );
      }
      setSignOutDialogOpen(false);
    } catch (err) {
      const detail =
        formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error(detail);
    } finally {
      setSignOutBusy(false);
    }
  };

  const initials = (user?.full_name || user?.email || "??")
    .split(/[\s.@]+/)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();

  // Phase 12 — glass blur on scroll
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-20 flex h-14 items-center gap-4 px-6 transition-all"
      style={{
        backgroundColor: scrolled
          ? "color-mix(in oklab, var(--surface) 78%, transparent)"
          : "var(--surface)",
        backdropFilter: scrolled ? "saturate(140%) blur(12px)" : "none",
        WebkitBackdropFilter: scrolled ? "saturate(140%) blur(12px)" : "none",
        borderBottom: `1px solid ${scrolled ? "var(--border-subtle)" : "transparent"}`,
      }}
    >
      <div className="flex items-center gap-2 text-sm" data-testid="breadcrumbs">
        <span
          className="font-serif text-[15px] font-semibold"
          style={{ letterSpacing: "-0.005em", color: "var(--page-fg)" }}
        >
          Goodwin
        </span>
        <span
          className="text-[10px] font-extrabold uppercase tracking-[0.14em]"
          style={{ color: "var(--success-fg)" }}
        >
          Resolve
        </span>
        {crumbs.map((c, i) => (
          <React.Fragment key={`${c}-${i}`}>
            <ChevronRight
              className="h-3.5 w-3.5"
              style={{ color: "var(--page-fg-muted)" }}
            />
            <span
              className="transition-colors"
              style={{
                color:
                  i === crumbs.length - 1
                    ? "var(--page-fg)"
                    : "var(--page-fg-secondary)",
                fontWeight: i === crumbs.length - 1 ? 500 : 400,
              }}
            >
              {c}
            </span>
          </React.Fragment>
        ))}
      </div>

      <div className="ml-auto flex items-center gap-2">
        <CmdKFirstSessionHint />
        <CmdKHint
          onClick={() => {
            const evt = new KeyboardEvent("keydown", {
              key: "k",
              metaKey: true,
              ctrlKey: true,
              bubbles: true,
            });
            document.dispatchEvent(evt);
          }}
        />
      </div>

      <NotificationsBell onUnreadChange={onUnreadChange} />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="user-menu-trigger"
            aria-label="User menu"
            className="flex items-center gap-2 rounded-full py-1 pl-1 pr-3 transition-colors hover:bg-[color:var(--surface-2)]"
            style={{
              border: "1px solid var(--border-subtle)",
              backgroundColor: "var(--surface)",
            }}
          >
            <span
              className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold text-white"
              style={{ backgroundColor: "#1E3A5F" }}
            >
              {initials}
            </span>
            <span
              className="hidden text-sm font-medium md:inline"
              style={{ color: "var(--page-fg)" }}
              data-testid="current-user-name"
            >
              {user?.full_name || user?.email}
            </span>
            <RoleBadge role={user?.role} />
            {user?.is_admin && user?.role === "hr" && (
              <span
                data-testid="admin-badge"
                className="ml-1 inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.10em]"
                style={{
                  backgroundColor: "var(--success-bg)",
                  color: "var(--success-fg)",
                  boxShadow: "inset 0 0 0 1px var(--success-border)",
                }}
              >
                Admin
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-56"
          style={{
            backgroundColor: "var(--surface)",
            borderColor: "var(--border-subtle)",
            boxShadow: "var(--shadow-modal)",
          }}
        >
          <DropdownMenuLabel>
            <div className="flex flex-col">
              <span className="text-sm font-semibold" style={{ color: "var(--page-fg)" }}>
                {user?.full_name}
              </span>
              <span className="text-xs" style={{ color: "var(--page-fg-secondary)" }}>
                {user?.email}
              </span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => navigate("/profile")}
            data-testid="user-menu-profile"
            className="text-sm"
          >
            <UserIcon className="mr-2 h-4 w-4" />
            My profile
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => navigate("/settings/security")}
            data-testid="user-menu-security"
            className="text-sm"
          >
            <SettingsIcon className="mr-2 h-4 w-4" />
            Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <ThemeQuickToggle />
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setSignOutDialogOpen(true);
            }}
            data-testid="logout-others-menu-item"
            className="text-sm"
          >
            <ShieldOff className="mr-2 h-4 w-4" />
            Sign me out of all other devices
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={handleLogout}
            data-testid="logout-menu-item"
            className="text-sm"
            style={{ color: "#B91C1C" }}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={signOutDialogOpen}
        onOpenChange={(open) => {
          if (!signOutBusy) setSignOutDialogOpen(open);
        }}
      >
        <AlertDialogContent data-testid="logout-others-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out of all other devices?</AlertDialogTitle>
            <AlertDialogDescription>
              Active sessions on every other browser, phone or tablet for
              <span className="font-semibold"> {user?.email} </span>
              will be revoked immediately. The current session on this device
              will stay signed in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="logout-others-cancel"
              disabled={signOutBusy}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="logout-others-confirm"
              disabled={signOutBusy}
              onClick={(e) => {
                e.preventDefault();
                handleSignOutOthers();
              }}
            >
              {signOutBusy ? "Signing out…" : "Sign out other devices"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
}

export default TopBar;
